/* eslint-disable camelcase */
import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Text } from '@lightspeed/flame/Text';

import { ApiErrorCodeMap } from './helpers/errors';
import { focusNextInput, keydownHandler, keydownHandlerRefactored } from './helpers/scan';
import { createReactRenderer } from 'helpers/react-renderer';
import { sanitizeText, toBoolean } from 'pages/utils/sanitize';
import { getDateFormat } from 'pages/utils/time';
import { hasFeature } from 'root/api/admin/features';

import {
  StyledItemDescription,
  StyledModal,
  StyledModalBody,
  StyledModalFooter,
  StyledModalHeader,
} from './AddSerial.style';
import { SerialInput } from './SerialInput';
import { SerializedData, SerializedFormData } from './types';
import { useSerialNumberForm } from './useSerialNumberForm';

// NOTE: business logic
// serials from the serialized_received table that are `checkedIn: true` are disabled because they've already been added to inventory
// `checkedIn: false` are editable / deletable until they're added to inventory
// the number of blank inputs depends on the numReceived PO's value minus the number of `checkedIn: false` serialized_received
// the "Date Received" corresponds to the date the serial was added to inventory (yellow button)

export const getButtonVariant = (
  numReceived: number,
  notCheckedIn: number,
  isError: boolean,
  hasRight: boolean
): string => {
  let variant = 'default';

  if ((isError && !hasRight && notCheckedIn !== numReceived) || notCheckedIn > numReceived) {
    variant = 'danger';
  } else if (notCheckedIn < numReceived) {
    variant = 'secondary';
  }

  return variant;
};

const AddSerialButton = ({
  purchase_item_id: purchaseItemId,
  description: itemDescription,
  isEnabled = false,
  num_received: numReceived,
  row_num: rowNum,
  serialized_received_not_checked_in,
  hasRight = true,
}: {
  purchase_item_id: number;
  description: string;
  isEnabled?: boolean;
  num_received: number;
  row_num: number;
  serialized_received_not_checked_in: number;
  hasRight: boolean;
}) => {
  const { id: accountId } = window.merchantos.account;
  const serializedReceivedNotCheckedIn = serialized_received_not_checked_in || 0;

  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [savingRow, setSavingRow] = React.useState<number | undefined>();
  const [isSubmitError] = React.useState(
    document.getElementById('notificationBarTypeWarning')?.offsetParent !== null || false
  );
  const [isErrorFetchingData, setIsErrorFetchingData] = React.useState(false);
  const [receivedValueError, setReceivedValueError] = React.useState(false);
  const [errorCode, setErrorCode] = React.useState<number | undefined>();
  const [updateReceivedValue, setUpdateReceivedValue] = React.useState(false);
  const [serializedData, setSerializedData] = React.useState<SerializedData[]>();
  const [numReceivedRows, setNumReceivedRows] = React.useState(numReceived);
  const [editableSerialInputs, setEditableSerialInputs] = React.useState<[] | SerializedData[]>([]);
  const [readOnlySerialInputs, setReadOnlySerialInputs] = React.useState<[] | SerializedData[]>([]);
  const [dateFormat, setDateFormat] = React.useState<string | undefined>();
  const [disabledRowIndexes, setDisabledRowIndexes] = React.useState<number[]>([]);
  const [buttonVariant, setButtonVariant] = React.useState(
    getButtonVariant(numReceived, serializedReceivedNotCheckedIn, isSubmitError, hasRight)
  );

  const [hasScanRefactor, setHasScanRefactor] = React.useState(false);
  React.useEffect(() => {
    hasFeature('InventoryCountScanRefactor').then((hasFeature) => {
      setHasScanRefactor(hasFeature);
    });
  }, []);

  const isAllInputDisabled = !isEnabled;
  const serialNumberForm = useSerialNumberForm({ editableSerialFields: editableSerialInputs });
  const { fields, update, errors, setError, clearErrors, reset } = serialNumberForm;
  const modifiedIndices = React.useRef<Set<number>>(new Set());

  React.useEffect(() => {
    const notCheckedInFields =
      (fields as SerializedFormData[])?.filter(
        ({ checkedIn }) => checkedIn && !toBoolean(checkedIn)
      )?.length || 0;

    setButtonVariant(
      getButtonVariant(
        numReceivedRows,
        fields.length ? notCheckedInFields : serializedReceivedNotCheckedIn,
        isSubmitError,
        hasRight
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numReceivedRows, fields]);

  React.useEffect(() => {
    const syncNumReceivedInput = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setNumReceivedRows(Number(value) || 0);
    };

    document
      .getElementById(`purchase_listings_checkin_items_view_num_received${rowNum}`)
      // @ts-ignore type is correct
      ?.addEventListener('change', syncNumReceivedInput);

    return () => {
      document
        .getElementById(`purchase_listings_checkin_items_view_num_received${rowNum}`)
        // @ts-ignore type is correct
        ?.removeEventListener('change', syncNumReceivedInput);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setIsLoading(true);
      setErrorCode(undefined);
      setReceivedValueError(false);
      setUpdateReceivedValue(false);
      setSerializedData(undefined);
      setIsErrorFetchingData(false);
      setDisabledRowIndexes([]);
      reset({ editableFields: fields }, { keepValues: true });
    } else {
      const getData = () => {
        Promise.all([
          fetch(`/API/Account/${accountId}/OrderLine/${purchaseItemId}.json`),
          fetch(`/API/Account/${accountId}/OrderLine/${purchaseItemId}/SerializedReceived.json`),
          getDateFormat(),
        ])
          .then(async ([res1, res2, res3]) => {
            const orderLine = await res1.json();
            const serializedOutput = await res2.json();
            const dateFormat = res3;
            const {
              OrderLine: { numReceived },
            } = orderLine;

            let serialized = [];
            const { SerializedReceivedOutput } = serializedOutput;
            if (SerializedReceivedOutput) {
              serialized = Array.isArray(serializedOutput.SerializedReceivedOutput)
                ? SerializedReceivedOutput
                : [SerializedReceivedOutput];
            }

            setNumReceivedRows(Number(numReceived));
            setSerializedData(serialized);
            setDateFormat(dateFormat);
          })
          .catch((_err) => {
            setIsErrorFetchingData(true);
          })
          .finally(() => {
            setIsLoading(false);
          });
      };

      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDelete = async (fieldID: string) => {
    const index = fields.map((field) => field.id).indexOf(fieldID);
    const {
      id,
      name,
      serializedReceivedID: serializedReceivedId,
    } = (fields?.find((data) => data.id === fieldID) || {}) as SerializedFormData;

    if (index > -1 && serializedReceivedId) {
      setSavingRow(index);
      try {
        const response = await fetch(
          `/API/Account/${accountId}/OrderLine/${purchaseItemId}/SerializedReceived/${serializedReceivedId}.json`,
          {
            method: 'DELETE',
          }
        );

        // By default window.fetch doesn't throw on bad status code
        if (!response?.status || response.status >= 400) {
          throw response;
        }

        update(index, { id, name });

        if (receivedValueError) {
          if (editableSerialInputs.length - (disabledRowIndexes.length + 1) <= numReceivedRows) {
            setReceivedValueError(false);
          }
          setDisabledRowIndexes((indexes) => [...indexes, index]);
        }

        focusNextInput();
      } catch (err) {
        const { errorCode } = await err.json();

        setErrorCode(errorCode);
        setError(
          `editableFields[${index}].name` as const,
          {
            type: 'custom',
            message:
              ApiErrorCodeMap[errorCode as keyof typeof ApiErrorCodeMap] || ApiErrorCodeMap.generic,
          },
          { shouldFocus: true }
        );
      }
    }

    setSavingRow(undefined);
  };

  const renderReadOnlySerialRows = () => {
    return readOnlySerialInputs.map((serialData, index) => {
      const { serial, timeStamp } = serialData || {};
      const rowNumber = editableSerialInputs.length + index + 1;
      return (
        <SerialInput
          key={serial}
          rowNumber={rowNumber}
          isInputDisabled={true}
          inputValue={serial}
          date={timeStamp}
          dateFormat={dateFormat}
          index={index}
          errors={null}
        />
      );
    });
  };

  const renderSerialRows = () => {
    return (fields as unknown as SerializedFormData[]).map((serialData, index) => {
      const {
        serializedReceivedID: serializedReceivedId,
        serial,
        checkedIn,
        id,
      } = serialData as unknown as SerializedFormData;

      const showDeleteIcon =
        (serial?.length && serializedReceivedId && toBoolean(checkedIn) === false) || false;

      return (
        <SerialInput
          key={id}
          rowNumber={index + 1}
          isRowDisabled={disabledRowIndexes.includes(index)}
          isSaving={savingRow === index}
          isInputDisabled={isAllInputDisabled || savingRow === index}
          inputValue={serial}
          dateFormat={dateFormat}
          index={index}
          onKeyDown={hasScanRefactor ? keydownHandlerRefactored : keydownHandler}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const {
              target: { value },
            } = event;

            modifiedIndices.current.add(index);

            update(index, {
              ...(serialData as unknown as SerializedData),
              serial: value,
            });

            checkForDupes(value, id, index);
          }}
          onBlur={async (event: React.ChangeEvent<HTMLInputElement>) => {
            const {
              target: { value },
            } = event;

            if (!serializedReceivedId && !value) {
              return;
            }

            if (
              isEnabled &&
              modifiedIndices.current.has(index) &&
              !(errors?.editableFields as Array<unknown> | undefined)?.[index]
            ) {
              modifiedIndices.current.delete(index);
              try {
                setSavingRow(index);
                setErrorCode(undefined);

                let response;
                let isDelete = false;

                if (serializedReceivedId) {
                  if (!value) {
                    isDelete = true;
                    response = await fetch(
                      `/API/Account/${accountId}/OrderLine/${purchaseItemId}/SerializedReceived/${serializedReceivedId}.json`,
                      {
                        method: 'DELETE',
                      }
                    );
                  } else {
                    response = await fetch(
                      `/API/Account/${accountId}/OrderLine/${purchaseItemId}/SerializedReceived/${serializedReceivedId}.json`,
                      {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ serial: value.trim() }),
                      }
                    );
                  }
                } else {
                  response = await fetch(
                    `/API/Account/${accountId}/OrderLine/${purchaseItemId}/SerializedReceived.json`,
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({ serial: value.trim() }),
                    }
                  );
                }

                // By default window.fetch doesn't throw on bad status code
                if (!response?.status || response.status >= 400) {
                  throw response;
                }

                let defaultValue = {};
                if (!isDelete) {
                  const { SerializedReceivedOutput }: { SerializedReceivedOutput: SerializedData } =
                    await response.json();

                  defaultValue = { ...serialData, ...SerializedReceivedOutput };
                } else {
                  const { name, id } = serialData;
                  defaultValue = { name, id };

                  if (receivedValueError) {
                    if (
                      editableSerialInputs.length - (disabledRowIndexes.length + 1) <=
                      numReceivedRows
                    ) {
                      setReceivedValueError(false);
                    }
                    setDisabledRowIndexes((indexes) => [...indexes, index]);
                  }

                  focusNextInput();
                }

                update(index, defaultValue);
                // @NOTE "fields" variable is no updated yet, simply replace at id by the new "defaultValue"
                reset({
                  editableFields: fields.map((field) => (field.id === id ? defaultValue : field)),
                });
              } catch (err) {
                const { errorCode } = await err.json();

                setErrorCode(errorCode);
                setError(
                  `editableFields[${index}].name` as const,
                  {
                    type: 'custom',
                    message:
                      ApiErrorCodeMap[errorCode as keyof typeof ApiErrorCodeMap] ||
                      ApiErrorCodeMap.generic,
                  },
                  { shouldFocus: true }
                );
              }

              setSavingRow(undefined);
            }
          }}
          errors={errors}
          showDeleteIcon={showDeleteIcon}
          onDelete={() => handleDelete(id)}
        />
      );
    });
  };

  const checkForDupes = React.useCallback(
    (value: string, fieldID: string, index: number) => {
      // @ts-ignore merging fields and readOnlySerialInputs is fine
      const isDupe = fields.concat(readOnlySerialInputs).find((field) => {
        const { serial, id } = field as SerializedFormData;
        if (serial !== '' && value === serial && fieldID !== id) return true;
        return false;
      });

      if (isDupe) {
        setError(
          `editableFields[${index}].name` as const,
          {
            type: 'custom',
            message: 'This serial number is already in use',
          },
          { shouldFocus: true }
        );
      } else {
        clearErrors(`editableFields[${index}]` as const);
      }

      return !isDupe;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields]
  );

  React.useEffect(() => {
    if (!isLoading) {
      let numberOfBlankInputRows = 0;
      let enabledSerializedInputs: SerializedData[] = [];
      const disabledSerializedInputs: SerializedData[] = [];

      if (numReceivedRows === 0 && serializedData?.length === 0) {
        // User must add the value they've received into the "# Received" input to begin recording serial numbers
        setUpdateReceivedValue(true);
        return;
      }

      serializedData?.forEach((serial) => {
        toBoolean(serial.checkedIn)
          ? disabledSerializedInputs.push(serial)
          : enabledSerializedInputs.push(serial);
      });

      if (numReceivedRows >= enabledSerializedInputs.length) {
        // User has indicated there are serialized items to receive
        numberOfBlankInputRows = numReceivedRows - enabledSerializedInputs.length;

        enabledSerializedInputs = [
          ...enabledSerializedInputs,
          ...Array.from(Array(numberOfBlankInputRows)),
        ];
      }

      setEditableSerialInputs(enabledSerializedInputs);
      setReadOnlySerialInputs(disabledSerializedInputs);

      if (enabledSerializedInputs.length > numReceivedRows) {
        // User must delete some entries to match numReceivedRows
        setReceivedValueError(true);
      } else {
        setReceivedValueError(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedData, isLoading]);

  return (
    <>
      <Button
        data-test="serial-button-modal"
        size="small"
        variant={buttonVariant}
        fill
        onClick={() => setIsOpen(true)}
        className={buttonVariant}
      >
        S/N
      </Button>
      <StyledModal
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        aside
        className="prevent-global-scan"
      >
        <StyledModalHeader data-test="serial-number-modal">
          {isLoading ? (
            <Bone height={3} width="100%" />
          ) : (
            <StyledItemDescription key="localizejs-fix">
              Serial numbers - <var>{sanitizeText(itemDescription)}</var>
            </StyledItemDescription>
          )}
        </StyledModalHeader>
        <Box style={{ position: 'sticky' }}>
          <Box>
            {isErrorFetchingData && (
              <Alert type="danger" m={4} noCloseBtn>
                Unable to load serial numbers. Try again.
              </Alert>
            )}
            {errorCode && (
              <Alert type="danger" m={4}>
                Unable to save serial number. Try again.
              </Alert>
            )}
            {updateReceivedValue && isEnabled && (
              <Alert type="info" m={4}>
                Number of items received must be above 0 to begin recording serial numbers
              </Alert>
            )}
            {receivedValueError && (
              <Alert type="danger" m={4} noCloseBtn>
                The number of items received is lower than the amount of serial numbers entered.
                <br />
                Delete any unassigned serial numbers to continue.
              </Alert>
            )}
          </Box>
          <Flex>
            <Box width="10%" p={4} minWidth="60px">
              <Text fontWeight="bold">#</Text>
            </Box>
            <Box width="45%" p={4} flex="0 0 auto">
              <Text fontWeight="bold">Serial number</Text>
            </Box>
            <Box width="30%" p={4}>
              <Text fontWeight="bold">Date received</Text>
            </Box>
            <Box width="15%" p={4} />
          </Flex>
          <Divider />
        </Box>
        <StyledModalBody>
          {isLoading ? (
            Array.from(Array(3).keys()).map((index) => (
              <div key={index}>
                <Flex>
                  <Box width="10%" p={4}>
                    <Bone />
                  </Box>
                  <Box width="45%" p={4}>
                    <Bone />
                  </Box>
                  <Box width="30%" p={4}>
                    <Bone />
                  </Box>
                  <Box width="15%" p={4}>
                    <Bone />
                  </Box>
                </Flex>
                <Divider />
              </div>
            ))
          ) : (
            <>
              <form id="serialsForm">
                <Flex flexDirection="column">
                  {fields && renderSerialRows()}
                  {renderReadOnlySerialRows()}
                </Flex>
              </form>
            </>
          )}
        </StyledModalBody>
        <StyledModalFooter>
          <Flex justifyContent="flex-end">
            <Button onClick={() => setIsOpen(false)} disabled={isLoading} data-test="cancel-button">
              Close
            </Button>
          </Flex>
        </StyledModalFooter>
      </StyledModal>
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: AddSerialButton,
  selector: '[data-react-root="AddSerialButton"]',
});

history.onChangeState(
  [
    {
      name: 'purchase.views.purchase',
      form_name: 'view',
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default AddSerialButton;
